import type { AxiosError } from 'axios'
import type { SendCalculationEmailRequest, SendContractEmailRequest, SendContractEmailResponse } from 'BrokerTsLib/src/dtos'
import { Product } from 'BrokerTsLib/src/enums'
import apiClient from 'BrokerTsLib/src/Utils/apiClient'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { baseUrl } from '.'
import { useApp } from './useApp'

export interface GetCalculationEmailResponse {
  logoUrl: string,
  header: string,
  footer: string,
  signature: string,
  disclaimer: string
}

export interface GetContractEmailResponse {
  logoUrl: string,
  email: string,
  header: string,
  recap: string,
  acceptation: string,
  documents: string,
  signature: string,
  disclaimer: string
}

const mailingVehicleApiUrl = import.meta.env.VITE_MAILING_VEHICLE_API
const mailingPropertyApiUrl = import.meta.env.VITE_MAILING_PROPERTY_API
const mailingLiabilityApiUrl = import.meta.env.VITE_MAILING_LIABILITY_API
const mailingTravelApiUrl = import.meta.env.VITE_MAILING_TRAVEL_API
export const useUrl = () => {
  const { product } = useApp()

  switch (product) {
    case Product.TravelInsurance:
      return `${baseUrl}/${mailingTravelApiUrl}`
    case Product.PropertyInsurance:
      return `${baseUrl}/${mailingPropertyApiUrl}`
    case Product.LiabilityInsurance:
      return `${baseUrl}/${mailingLiabilityApiUrl}`
    default:
      return `${baseUrl}/${mailingVehicleApiUrl}`
  }
}

export function useGetCalculationEmail<T = GetCalculationEmailResponse>(paramsId?: string, enabled?: boolean) {
  const url = useUrl()

  return useQuery<T, AxiosError>({
    queryKey: ['GetCalculationEmail', paramsId],
    queryFn: () =>
      apiClient.get<T>(`${url}/GetCalculationEmail`, { params: { paramsId } }).then(res => res.data),
    enabled: !!paramsId && enabled,
  })
}

export const useSendCalculationEmail = () => {
  const url = useUrl()

  return useMutation((value: SendCalculationEmailRequest) =>
    apiClient.post(`${url}/SendCalculationEmail`, value),
  )
}

export function useGetContractEmail<T = GetContractEmailResponse>(paramsId?: string, insuranceId?: number, enabled?: boolean) {
  const url = useUrl()

  return useQuery<T, AxiosError>({
    queryKey: ['GetContractEmail', paramsId, insuranceId],
    queryFn: () =>
      apiClient.get<T>(`${url}/GetContractEmail`, { params: { paramsId, insuranceId } }).then(res => res.data),
    enabled: !!paramsId && enabled,
  })
}

export const useSendContractEmail = () => {
  const url = useUrl()
  const queryClient = useQueryClient()
  const { product } = useApp()
  return useMutation({
    mutationFn: (value: SendContractEmailRequest) =>
      apiClient.post<SendContractEmailResponse>(`${url}/SendContractEmail`, value),
    onSuccess: (_, { paramsId }) => {
      queryClient.invalidateQueries(['useGetContractEmailLogs', paramsId, product])
    }
  })
}